import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { XIcon, CheckCircle2Icon, CopyIcon, Share2Icon } from "lucide-react";
import { MobyNetLogo } from "./moby-net-logo";

// ============================================================================
// BetaSignupDialog
// ============================================================================
// Three-step modal surfaced from OnboardingPage when the URL contains
// `?beta=1` (typically from a QR scan). Collects waitlist data, verifies
// email with a 6-digit code, then shows success with share + position.
//
// This dialog is strictly additive — it overlays the existing onboarding page
// and is fully dismissible. No writes occur to the invite/wallet/EULA flow.
//
// Design doc: docs/md-vault/beta-waitlist-design.md
// ============================================================================

type Step = "form" | "verify" | "success";

type Region =
  | "americas" | "emea" | "apac" | "latam" | "africa" | "oceania";

type UseCase =
  | "cinematographer"
  | "content_creator"
  | "videogame_streamer"
  | "architectural_design"
  | "data_sovereignty"
  | "direct_p2p"
  | "research_science"
  | "conservation_rescue"
  | "enterprise"
  | "developer"
  | "journalist"
  | "humanitarian"
  | "legal_evidence"
  | "education"
  | "other";

type DiscoveryChannel =
  | "qr_event"
  | "social"
  | "word_of_mouth"
  | "press"
  | "search"
  | "partner_referral"
  | "other";

const USE_CASE_OPTIONS: { value: UseCase; label: string }[] = [
  { value: "cinematographer", label: "Cinematographer / Filmmaker" },
  { value: "content_creator", label: "Content Creator" },
  { value: "videogame_streamer", label: "Videogame Streamer" },
  { value: "architectural_design", label: "Architectural Design / AEC" },
  { value: "data_sovereignty", label: "Data Sovereignty / Autonomy" },
  { value: "direct_p2p", label: "Direct P2P Communications" },
  { value: "research_science", label: "Research / Science" },
  { value: "conservation_rescue", label: "Conservation / Rescue Ops" },
  { value: "enterprise", label: "Enterprise / Team Collaboration" },
  { value: "developer", label: "Developer / Technical Integrator" },
  { value: "journalist", label: "Journalist / Press" },
  { value: "humanitarian", label: "Humanitarian / NGO Field Ops" },
  { value: "legal_evidence", label: "Legal / Chain-of-Custody Evidence" },
  { value: "education", label: "Education / Remote Classroom" },
  { value: "other", label: "Other" },
];

const REGION_OPTIONS: { value: Region; label: string }[] = [
  { value: "americas", label: "Americas" },
  { value: "emea", label: "Europe, Middle East, Africa (EMEA)" },
  { value: "apac", label: "Asia Pacific (APAC)" },
  { value: "latam", label: "Latin America" },
  { value: "africa", label: "Africa" },
  { value: "oceania", label: "Oceania" },
];

const DISCOVERY_OPTIONS: { value: DiscoveryChannel; label: string }[] = [
  { value: "qr_event", label: "QR code at an event" },
  { value: "social", label: "Social media" },
  { value: "word_of_mouth", label: "Word of mouth / friend" },
  { value: "press", label: "Press / article" },
  { value: "search", label: "Web search" },
  { value: "partner_referral", label: "Partner / ambassador" },
  { value: "other", label: "Other" },
];

interface FormState {
  email: string;
  name: string;
  region: Region | "";
  country: string;
  useCase: UseCase | "";
  discoveryChannel: DiscoveryChannel | "";
  eventId: string;
  referrer: string;
  message: string;
  consent: boolean;
}

interface BetaSignupDialogProps {
  open: boolean;
  onClose: () => void;
  initialEventId?: string | null;
  initialReferrer?: string | null;
  gatewayBaseUrl?: string; // defaults to window.location.origin
}

export function BetaSignupDialog({
  open,
  onClose,
  initialEventId,
  initialReferrer,
  gatewayBaseUrl,
}: BetaSignupDialogProps) {
  const [step, setStep] = useState<Step>("form");
  const [submitting, setSubmitting] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [successData, setSuccessData] = useState<{
    waitlistId: string;
    position: number;
    referralCount: number;
  } | null>(null);

  const [form, setForm] = useState<FormState>(() => ({
    email: "",
    name: "",
    region: "",
    country: "",
    useCase: "",
    discoveryChannel: initialEventId ? "qr_event" : "",
    eventId: initialEventId || "",
    referrer: initialReferrer || "",
    message: "",
    consent: false,
  }));

  const gatewayUrl = useMemo(() => {
    if (gatewayBaseUrl) return gatewayBaseUrl.replace(/\/$/, "");
    const stored = localStorage.getItem("moby_pref_emailGatewayUrl")?.trim();
    if (stored) return stored.replace(/\/$/, "");
    return window.location.origin;
  }, [gatewayBaseUrl]);

  // Resend cooldown ticker
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((n) => Math.max(0, n - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  // Reset state when dialog closes
  useEffect(() => {
    if (open) return;
    // small delay so user doesn't see form reset during fade-out
    const t = setTimeout(() => {
      setStep("form");
      setVerifyCode("");
      setVerifyError(null);
      setSuccessData(null);
      setSubmitting(false);
    }, 200);
    return () => clearTimeout(t);
  }, [open]);

  const updateForm = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const canSubmitForm =
    !!form.email.trim() &&
    !!form.name.trim() &&
    !!form.region &&
    !!form.useCase &&
    !!form.discoveryChannel &&
    form.consent;

  const requestCode = useCallback(async () => {
    if (!canSubmitForm || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${gatewayUrl}/api/beta/request-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email.trim(),
          name: form.name.trim(),
          region: form.region,
          country: form.country.trim() || undefined,
          useCase: form.useCase,
          discoveryChannel: form.discoveryChannel,
          eventId: form.eventId || undefined,
          referrer: form.referrer.trim() || undefined,
          message: form.message.trim() || undefined,
          consent: form.consent,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast.error(data.error || "Could not send verification code");
        return;
      }
      if (data.alreadyVerified && data.waitlistId) {
        // Already on the list — jump straight to success view.
        // We don't know position/referralCount from this endpoint, fetch them.
        try {
          const infoRes = await fetch(`${gatewayUrl}/api/beta/waitlist/${data.waitlistId}`);
          const info = await infoRes.json();
          if (info.success) {
            setSuccessData({
              waitlistId: data.waitlistId,
              position: info.position,
              referralCount: info.referralCount,
            });
          } else {
            setSuccessData({ waitlistId: data.waitlistId, position: 0, referralCount: 0 });
          }
        } catch {
          setSuccessData({ waitlistId: data.waitlistId, position: 0, referralCount: 0 });
        }
        setStep("success");
        return;
      }
      toast.success("Verification code sent. Check your inbox.");
      setResendCooldown(60);
      setStep("verify");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Network error");
    } finally {
      setSubmitting(false);
    }
  }, [canSubmitForm, submitting, gatewayUrl, form]);

  const resendCode = useCallback(async () => {
    if (resendCooldown > 0 || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${gatewayUrl}/api/beta/resend-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast.error(data.error || "Could not resend code");
        return;
      }
      toast.success("New code sent.");
      setResendCooldown(60);
      setVerifyCode("");
      setVerifyError(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Network error");
    } finally {
      setSubmitting(false);
    }
  }, [resendCooldown, submitting, gatewayUrl, form.email]);

  const verifyCodeFn = useCallback(async () => {
    if (!/^\d{6}$/.test(verifyCode) || submitting) return;
    setSubmitting(true);
    setVerifyError(null);
    try {
      const res = await fetch(`${gatewayUrl}/api/beta/verify-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email.trim(), code: verifyCode }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setVerifyError(data.error || "Incorrect code");
        return;
      }
      setSuccessData({
        waitlistId: data.waitlistId,
        position: data.position || 0,
        referralCount: data.referralCount || 0,
      });
      setStep("success");
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : "Network error");
    } finally {
      setSubmitting(false);
    }
  }, [verifyCode, submitting, gatewayUrl, form.email]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="beta-dialog-title"
    >
      <div className="relative w-full max-w-lg max-h-[92vh] overflow-hidden bg-surface rounded-2xl shadow-2xl border border-outline-variant flex flex-col">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-surface-variant transition-colors z-10"
          aria-label="Close"
          title="Close"
        >
          <XIcon size={20} />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 px-6 pt-6 pb-3 border-b border-outline-variant">
          <MobyNetLogo className="h-10" />
          <div>
            <h2 id="beta-dialog-title" className="text-title-md text-on-surface font-semibold">
              Request Early Access to MobyChat Beta
            </h2>
            <p className="text-label-sm text-on-surface-variant">
              {step === "form" && "Tell us a bit about you."}
              {step === "verify" && "Verify your email."}
              {step === "success" && "You're on the list."}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === "form" && (
            <FormStep
              form={form}
              updateForm={updateForm}
              eventLocked={!!initialEventId}
              referrerLocked={!!initialReferrer}
            />
          )}
          {step === "verify" && (
            <VerifyStep
              email={form.email}
              code={verifyCode}
              setCode={setVerifyCode}
              error={verifyError}
              resendCooldown={resendCooldown}
              onResend={resendCode}
              onBack={() => setStep("form")}
            />
          )}
          {step === "success" && successData && (
            <SuccessStep
              waitlistId={successData.waitlistId}
              position={successData.position}
              name={form.name || ""}
              referredByPresent={!!initialReferrer}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-outline-variant bg-surface-variant/30">
          {step === "form" && (
            <>
              <button
                onClick={onClose}
                className="text-label-lg text-on-surface-variant hover:text-on-surface"
              >
                Maybe later
              </button>
              <button
                onClick={requestCode}
                disabled={!canSubmitForm || submitting}
                className="px-5 py-2 rounded-full bg-primary text-on-primary text-label-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
              >
                {submitting ? "Sending…" : "Send verification code"}
              </button>
            </>
          )}
          {step === "verify" && (
            <>
              <button
                onClick={() => setStep("form")}
                className="text-label-lg text-on-surface-variant hover:text-on-surface"
              >
                Back
              </button>
              <button
                onClick={verifyCodeFn}
                disabled={!/^\d{6}$/.test(verifyCode) || submitting}
                className="px-5 py-2 rounded-full bg-primary text-on-primary text-label-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
              >
                {submitting ? "Verifying…" : "Verify"}
              </button>
            </>
          )}
          {step === "success" && (
            <button
              onClick={onClose}
              className="ml-auto px-5 py-2 rounded-full bg-primary text-on-primary text-label-lg font-medium hover:opacity-90 transition-opacity"
            >
              Done
            </button>
          )}
        </div>

        {/* Brand attribution — persistent across all steps */}
        <div className="px-6 py-2 text-center border-t border-outline-variant/40 bg-surface-variant/20">
          <span className="text-label-sm text-on-surface-variant">
            Powered by <strong className="text-on-surface">MobyNet</strong>, The People's Internet&trade;
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// Step 1: Form
// ---------------------------------------------------------------------------
function FormStep({
  form, updateForm, eventLocked, referrerLocked,
}: {
  form: FormState;
  updateForm: <K extends keyof FormState>(k: K, v: FormState[K]) => void;
  eventLocked: boolean;
  referrerLocked: boolean;
}) {
  return (
    <div className="flex flex-col gap-4">
      <Field label="Email" required>
        <input
          type="email"
          autoComplete="email"
          value={form.email}
          onChange={(e) => updateForm("email", e.target.value)}
          className={inputCls}
          placeholder="you@example.com"
        />
      </Field>
      <Field label="Name" required>
        <input
          type="text"
          autoComplete="name"
          value={form.name}
          onChange={(e) => updateForm("name", e.target.value)}
          className={inputCls}
          placeholder="How should we greet you?"
          maxLength={80}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Region" required>
          <select
            value={form.region}
            onChange={(e) => updateForm("region", e.target.value as Region)}
            className={inputCls}
            aria-label="Region"
          >
            <option value="">Select…</option>
            {REGION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Country (optional)">
          <input
            type="text"
            value={form.country}
            onChange={(e) => updateForm("country", e.target.value)}
            className={inputCls}
            placeholder="e.g. United States"
            maxLength={64}
          />
        </Field>
      </div>
      <Field label="Primary use case" required>
        <select
          value={form.useCase}
          onChange={(e) => updateForm("useCase", e.target.value as UseCase)}
          className={inputCls}
          aria-label="Primary use case"
        >
          <option value="">Select…</option>
          {USE_CASE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </Field>
      <Field label="How did you hear about us?" required>
        <select
          value={form.discoveryChannel}
          onChange={(e) => updateForm("discoveryChannel", e.target.value as DiscoveryChannel)}
          className={inputCls}
          disabled={eventLocked}
          aria-label="How did you hear about us"
        >
          <option value="">Select…</option>
          {DISCOVERY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </Field>
      {(form.discoveryChannel === "qr_event" || eventLocked) && (
        <Field label="Event name (optional)">
          <input
            type="text"
            value={form.eventId}
            onChange={(e) => updateForm("eventId", e.target.value)}
            className={inputCls}
            disabled={eventLocked}
            placeholder="e.g. Web Summit 2026, Tech Symposium…"
            maxLength={64}
            aria-label="Event name"
          />
        </Field>
      )}
      <Field label="Referred by (optional)">
        <input
          type="text"
          value={form.referrer}
          onChange={(e) => updateForm("referrer", e.target.value)}
          className={inputCls}
          placeholder="Friend's name, email, or MobyNet handle"
          maxLength={120}
          disabled={referrerLocked}
        />
        {!referrerLocked && (
          <span className="text-label-sm text-on-surface-variant">
            We'll auto-connect you once you're both on MobyChat.
          </span>
        )}
      </Field>
      <Field label="Message (optional)">
        <textarea
          value={form.message}
          onChange={(e) => updateForm("message", e.target.value)}
          className={inputCls}
          placeholder="What would you like us to know?"
          maxLength={280}
          rows={3}
        />
        <div className="text-label-sm text-on-surface-variant text-right">
          {form.message.length}/280
        </div>
      </Field>
      <label className="flex items-start gap-2 text-body-sm text-on-surface-variant cursor-pointer mt-1">
        <input
          type="checkbox"
          checked={form.consent}
          onChange={(e) => updateForm("consent", e.target.checked)}
          className="mt-1"
        />
        <span>
          I agree to receive beta communications from MobyNet. I understand my information is used
          solely for the MobyChat beta waitlist and that I may opt out at any time.
        </span>
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2: Verify Code
// ---------------------------------------------------------------------------
function VerifyStep({
  email, code, setCode, error, resendCooldown, onResend, onBack,
}: {
  email: string;
  code: string;
  setCode: (v: string) => void;
  error: string | null;
  resendCooldown: number;
  onResend: () => void;
  onBack: () => void;
}) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (idx: number, v: string) => {
    const digit = v.replace(/\D/g, "").slice(-1);
    const arr = code.split("");
    while (arr.length < 6) arr.push("");
    arr[idx] = digit;
    const next = arr.join("").slice(0, 6);
    setCode(next);
    if (digit && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted) {
      e.preventDefault();
      setCode(pasted);
      const idx = Math.min(pasted.length, 5);
      inputsRef.current[idx]?.focus();
    }
  };

  return (
    <div className="flex flex-col gap-4 items-center text-center">
      <p className="text-body-md text-on-surface">
        We sent a 6-digit code to <strong className="text-primary">{email}</strong>. Enter it below.
      </p>
      <div className="flex gap-2 justify-center">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <input
            key={i}
            ref={(el) => { inputsRef.current[i] = el; }}
            type="text"
            inputMode="numeric"
            pattern="\d*"
            maxLength={1}
            value={code[i] || ""}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            autoFocus={i === 0}
            aria-label={`Verification code digit ${i + 1}`}
            className="w-11 h-14 text-center text-title-lg font-semibold rounded-lg border border-outline-variant bg-surface-variant focus:border-primary focus:outline-none"
          />
        ))}
      </div>
      {error && (
        <p className="text-label-md text-error">{error}</p>
      )}
      <div className="flex items-center gap-2 text-label-md text-on-surface-variant">
        <span>Didn't get it?</span>
        <button
          onClick={onResend}
          disabled={resendCooldown > 0}
          className="text-primary font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:underline"
        >
          {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
        </button>
      </div>
      <button
        onClick={onBack}
        className="text-label-sm text-on-surface-variant hover:text-on-surface underline mt-2"
      >
        Wrong email? Go back
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3: Success
// ---------------------------------------------------------------------------
function SuccessStep({
  waitlistId, position, name, referredByPresent,
}: {
  waitlistId: string;
  position: number;
  name: string;
  referredByPresent: boolean;
}) {
  const shareUrl = `${window.location.origin}/onboarding?beta=1&ref=${encodeURIComponent(waitlistId)}`;

  const copyShare = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied");
    } catch {
      toast.error("Could not copy link");
    }
  }, [shareUrl]);

  const shareNative = useCallback(async () => {
    if (typeof navigator.share !== "function") {
      copyShare();
      return;
    }
    try {
      await navigator.share({
        title: "MobyChat Beta",
        text: "Join me on the MobyChat beta waitlist — A Direct and Secure communication suite.",
        url: shareUrl,
      });
    } catch {
      // user cancelled — silent
    }
  }, [shareUrl, copyShare]);

  return (
    <div className="flex flex-col gap-4 items-center text-center">
      <div className="w-16 h-16 rounded-full bg-success/15 flex items-center justify-center">
        <CheckCircle2Icon size={36} className="text-success" />
      </div>
      <h3 className="text-title-md font-semibold text-on-surface">
        You're in the pod{name ? `, ${name.split(" ")[0]}` : ""}.
      </h3>
      <p className="text-body-md text-on-surface-variant max-w-sm">
        When invite beta launches, we'll email your unique invite code.
        {referredByPresent && " Thanks for being invited by a friend."}
      </p>

      {position > 0 && (
        <div className="flex items-center gap-4 px-5 py-3 rounded-xl bg-surface-variant/60 border border-outline-variant">
          <div>
            <div className="text-label-sm text-on-surface-variant">Your position</div>
            <div className="text-title-lg font-bold text-primary">#{position}</div>
          </div>
          <div className="w-px h-8 bg-outline-variant" />
          <div>
            <div className="text-label-sm text-on-surface-variant">Waitlist ID</div>
            <div className="text-label-lg font-mono text-on-surface">{waitlistId}</div>
          </div>
        </div>
      )}

      <div className="w-full flex flex-col gap-2 mt-2">
        <p className="text-label-md text-on-surface-variant">
          Every friend you refer will automatically connect with you on launch day.
        </p>
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-variant border border-outline-variant">
          <input
            type="text"
            readOnly
            value={shareUrl}
            className="flex-1 bg-transparent text-label-sm text-on-surface outline-none min-w-0 truncate"
            aria-label="Your share link"
          />
          <button
            onClick={copyShare}
            className="p-1.5 rounded hover:bg-surface transition-colors"
            aria-label="Copy link"
            title="Copy link"
          >
            <CopyIcon size={16} />
          </button>
          <button
            onClick={shareNative}
            className="p-1.5 rounded hover:bg-surface transition-colors"
            aria-label="Share"
            title="Share"
          >
            <Share2Icon size={16} />
          </button>
        </div>
      </div>

      <p className="text-label-sm text-on-surface-variant mt-2">
        Thanks for joining MobyChat and the decentralized world of direct communications.
        Powered by MobyNet™, The People's Internet. 
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------
function Field({
  label, required, children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-label-sm text-on-surface-variant">
        {label}
        {required && <span className="text-primary ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full px-3 py-2 rounded border border-outline-variant bg-surface text-on-surface text-body-sm focus:border-primary focus:outline-none disabled:opacity-60";
